import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface PlantInfo {
  name: string;
  light: string;
  water: string;
  icon: string;
  color: string;
}

interface CareFactor {
  name: string;
  description: string;
  icon: string;
  color: string;
}

interface PurificationPlant {
  name: string;
  pollutant: string;
  efficiency: string;
  color: string;
}

interface PlantFact {
  label: string;
  icon: string;
}

interface HousePlantTemplateProps {
  topic: string;
  subtitle: string;
  plants: PlantInfo[];
  careFactors: CareFactor[];
  purificationData: PurificationPlant[];
  plantFacts: PlantFact[];
  photosynthesisSteps: { label: string; icon: string }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: HousePlantTemplateProps = {
  topic: '室内植物养护科学',
  subtitle: 'Indoor Plant Care Science',
  plants: [
    {
      name: '龟背竹',
      light: '散射光',
      water: '每周1-2次',
      icon: '🌿',
      color: '#22c55e',
    },
    {
      name: '琴叶榕',
      light: '明亮间接光',
      water: '表土干透浇透',
      icon: '🌳',
      color: '#16a34a',
    },
    {
      name: '虎皮兰',
      light: '耐阴/散射光',
      water: '每2-3周一次',
      icon: '🪴',
      color: '#4ade80',
    },
    {
      name: '多肉植物',
      light: '充足直射光',
      water: '每月2-3次',
      icon: '🌵',
      color: '#86efac',
    },
  ],
  careFactors: [
    {
      name: '光照管理',
      description: '根据植物类型选择直射光或散射光',
      icon: '☀️',
      color: '#fbbf24',
    },
    {
      name: '浇水原则',
      description: '见干见湿，避免积水导致根腐',
      icon: '💧',
      color: '#38bdf8',
    },
    {
      name: '温度控制',
      description: '多数室内植物适宜15-28°C',
      icon: '🌡️',
      color: '#f87171',
    },
    {
      name: '施肥周期',
      description: '生长季每月一次，休眠期停肥',
      icon: '🧪',
      color: '#a78bfa',
    },
  ],
  purificationData: [
    {
      name: '虎皮兰',
      pollutant: '甲醛',
      efficiency: '去除率87%',
      color: '#4ade80',
    },
    {
      name: '绿萝',
      pollutant: '苯/甲苯',
      efficiency: '去除率73%',
      color: '#22c55e',
    },
    {
      name: '常春藤',
      pollutant: '三氯乙烯',
      efficiency: '去除率90%',
      color: '#16a34a',
    },
    {
      name: '白掌',
      pollutant: '氨气',
      efficiency: '去除率80%',
      color: '#166534',
    },
  ],
  plantFacts: [
    { label: '光合作用', icon: '🔬' },
    { label: '蒸腾调湿', icon: '💨' },
    { label: '负离子释放', icon: '⚡' },
    { label: '心理疗愈', icon: '🧘' },
  ],
  photosynthesisSteps: [
    { label: 'CO₂ 吸收', icon: '🌬️' },
    { label: '光能捕获', icon: '☀️' },
    { label: '水分解', icon: '💧' },
    { label: 'O₂ 释放', icon: '🫧' },
    { label: '葡萄糖合成', icon: '🍬' },
  ],
  stats: [
    { value: 'NASA', label: '空气净化研究来源' },
    { value: '87%', label: '虎皮兰甲醛去除率' },
    { value: '15-28°C', label: '多数植物适宜温度' },
  ],
  bottomQuote:
    '每一片叶子都是一座微型化工厂——它们沉默地将阳光编织成生命，将废气转化为清新的空气',
};

export const HousePlantTemplate: React.FC<
  Partial<HousePlantTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const plantStart = 30;
  const careStart = 160;
  const photoStart = 200;
  const purifyStart = 280;
  const factStart = 370;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const photoDraw = interpolate(
    frame,
    [photoStart, photoStart + 100],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #052e16 0%, #011a0a 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating leaf particles */}
      {Array.from({ length: 55 }).map((_, i) => {
        const x = (i * 307) % 1920;
        const y = (i * 173) % 1080;
        const size = 3 + (i % 5);
        const drift = Math.sin(frame * 0.02 + i) * 12;
        const sway = Math.cos(frame * 0.015 + i * 0.5) * 6;
        const pulse = 0.06 + 0.05 * Math.sin(frame * 0.04 + i * 0.7);
        const colors = ['#22c55e', '#4ade80', '#86efac', '#16a34a'];
        const rotation = Math.sin(frame * 0.01 + i * 0.3) * 30;
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x + sway,
              top: y + drift,
              width: size,
              height: size * 1.5,
              borderRadius: '50% 50% 50% 0',
              background: colors[i % colors.length],
              opacity: pulse,
              transform: `rotate(${rotation}deg)`,
              boxShadow: `0 0 ${size * 2}px ${colors[i % colors.length]}30`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#166534',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#dcfce7',
            flexShrink: 0,
          }}
        >
          🌱 植物科学
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#dcfce7',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 26,
            color: '#6b8f71',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Popular Plants ===== */}
      <div style={{ position: 'absolute', top: 150, left: 36, width: 380 }}>
        <div
          style={{
            fontSize: 26,
            color: '#6b8f71',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          热门室内植物
        </div>
        <svg width="400" height="520" viewBox="0 0 400 520">
          <defs>
            <linearGradient id="plantBgGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#22c55e" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#166534" stopOpacity="0.05" />
            </linearGradient>
          </defs>

          <rect
            x="10"
            y="10"
            width="380"
            height="500"
            rx="12"
            fill="url(#plantBgGrad)"
            strokeWidth="2"
            stroke="#16653440"
            opacity={interpolate(frame, [plantStart, plantStart + 40], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />

          {p.plants.map((pl, i) => {
            const yPos = 30 + i * 120;
            const s = spring({
              frame: frame - (plantStart + 20 + i * 14),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <g key={i} opacity={s}>
                <circle
                  cx="55"
                  cy={yPos + 40}
                  r="28"
                  fill={`${pl.color}20`}
                  stroke={`${pl.color}40`}
                  strokeWidth="1.5"
                />
                <text
                  x="55"
                  y={yPos + 50}
                  fill={pl.color}
                  fontSize="30"
                  textAnchor="middle"
                >
                  {pl.icon}
                </text>

                <text
                  x="100"
                  y={yPos + 25}
                  fill="#dcfce7"
                  fontSize="24"
                  fontWeight="800"
                >
                  {pl.name}
                </text>
                <text x="100" y={yPos + 57} fill="#6b8f71" fontSize="22">
                  ☀️ {pl.light}
                </text>
                <text x="100" y={yPos + 87} fill="#6b8f71" fontSize="22">
                  💧 {pl.water}
                </text>

                <line
                  x1="95"
                  y1={yPos + 100}
                  x2="370"
                  y2={yPos + 100}
                  stroke={pl.color}
                  strokeWidth="1"
                  opacity="0.15"
                />
              </g>
            );
          })}
        </svg>
      </div>

      {/* ===== CENTER: Care Factors + Photosynthesis Diagram ===== */}
      <div style={{ position: 'absolute', top: 150, left: 480, width: 410 }}>
        <div
          style={{
            fontSize: 26,
            color: '#6b8f71',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          养护四大要素
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.careFactors.map((cf, i) => {
            const s = spring({
              frame: frame - (careStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: '#052e1690',
                  borderRadius: 14,
                  padding: '12px 16px',
                  border: `1.5px solid ${cf.color}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    background: `${cf.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 30,
                    flexShrink: 0,
                    boxShadow: `0 0 18px ${cf.color}30`,
                  }}
                >
                  {cf.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span
                      style={{ fontSize: 22, fontWeight: 800, color: cf.color }}
                    >
                      {cf.name}
                    </span>
                  </div>
                  <div
                    style={{ fontSize: 20, color: '#6b8f71', marginTop: 2 }}
                  >
                    {cf.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>

      {/* ===== CENTER-RIGHT: Photosynthesis Diagram ===== */}
      <div style={{ position: 'absolute', top: 150, left: 910, width: 580 }}>
        <div
          style={{
            fontSize: 26,
            color: '#6b8f71',
            marginBottom: 12,
            letterSpacing: '0.08em',
          }}
        >
          光合作用流程
        </div>
        <svg width="580" height="340" viewBox="0 0 800 440">
          {/* Central leaf shape */}
          <ellipse
            cx="400"
            cy="220"
            rx="145"
            ry="78"
            fill="#22c55e"
            opacity={0.12 * photoDraw}
            stroke="#22c55e"
            strokeWidth="2.5"
            strokeOpacity={0.3 * photoDraw}
          />
          <text
            x="400"
            y="230"
            fill="#4ade80"
            fontSize="26"
            textAnchor="middle"
            fontWeight="700"
            opacity={photoDraw}
          >
            叶绿体
          </text>

          {/* Steps arranged around */}
          {p.photosynthesisSteps.map((step, i) => {
            const angle = -90 + (i * 360) / p.photosynthesisSteps.length;
            const rad = (angle * Math.PI) / 180;
            const rx = 330;
            const ry = 155;
            const sx = 400 + rx * Math.cos(rad);
            const sy = 220 + ry * Math.sin(rad);
            const stepS = spring({
              frame: frame - (photoStart + 20 + i * 15),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <g key={i} opacity={stepS}>
                <line
                  x1={400 + 158 * Math.cos(rad)}
                  y1={220 + 90 * Math.sin(rad)}
                  x2={sx - 75 * Math.cos(rad)}
                  y2={sy - 52 * Math.sin(rad)}
                  stroke="#4ade80"
                  strokeWidth="2"
                  strokeDasharray="4 3"
                  opacity="0.4"
                />
                <circle
                  cx={sx}
                  cy={sy}
                  r="72"
                  fill="#052e16"
                  stroke="#22c55e40"
                  strokeWidth="2"
                />
                <text
                  x={sx}
                  y={sy - 10}
                  fill="white"
                  fontSize="26"
                  textAnchor="middle"
                >
                  {step.icon}
                </text>
                <text
                  x={sx}
                  y={sy + 24}
                  fill="#86efac"
                  fontSize="26"
                  textAnchor="middle"
                  fontWeight="600"
                >
                  {step.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* ===== RIGHT COLUMN: Purification Data + Plant Facts ===== */}
      <div style={{ position: 'absolute', top: 150, right: 36, width: 360 }}>
        <div
          style={{
            fontSize: 26,
            color: '#6b8f71',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          NASA空气净化研究
        </div>
        {p.purificationData.map((pd, i) => {
          const s = spring({
            frame: frame - (purifyStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#052e1690',
                borderRadius: 12,
                padding: '12px 16px',
                border: `1.5px solid ${pd.color}30`,
                marginBottom: 8,
                opacity: s,
                transform: `translateY(${(1 - s) * 16}px)`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 24, fontWeight: 800, color: pd.color }}>
                  {pd.name}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                <span style={{ fontSize: 22, color: '#6b8f71' }}>
                  目标: {pd.pollutant}
                </span>
                <span style={{ fontSize: 22, color: pd.color, fontWeight: 700 }}>
                  {pd.efficiency}
                </span>
              </div>
            </div>
          );
        })}

        {/* Plant Facts */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 24,
              color: '#6b8f71',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            植物益处
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.plantFacts.map((fact, i) => {
              const s = spring({
                frame: frame - (factStart + i * 12),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#052e1690',
                    borderRadius: 12,
                    padding: '14px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    border: '1px solid #16653440',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <span style={{ fontSize: 28, flexShrink: 0 }}>{fact.icon}</span>
                  <span
                    style={{ fontSize: 22, fontWeight: 700, color: '#4ade80' }}
                  >
                    {fact.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#052e1690',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #16653440',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{ fontSize: 30, fontWeight: 900, color: '#22c55e' }}
              >
                {st.value}
              </div>
              <div style={{ fontSize: 24, color: '#6b8f71', marginTop: 4 }}>
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#052e1690',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #166534',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🌿</span>
          <span style={{ fontSize: 22, color: '#dcfce7', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #166534, #22c55e, #86efac)',
        }}
      />
    </AbsoluteFill>
  );
};
